import * as React from 'react';
import { Col } from 'react-bootstrap';
import { faEdit, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '../../Parts/Button';
import { UsePortalDispatch } from '../../Store';
import { deleteQuoteAwning } from '../../Store/entities/awnings';
import { useQuoteById, useQuoteTypeNameByQuoteId } from '../../Store/entities/quotes/hooks';
import EditAwningModal from '../../Modals/AwningModals/EditAwningModal';
import AwningDuplicate from './AwningDuplicate';
import AwningSummary from './AwningSummary';
import type { fakeRootType } from '../../FakeRootType';

type AwningActionsProps = {
	quoteId: number;
	lineNumber: number;
	ROOT: fakeRootType;
	displayName: string;
};

const AwningAction = ({ quoteId, lineNumber, ROOT, displayName }: AwningActionsProps) => {
	const dispatch = UsePortalDispatch();
	const quote = useQuoteById(quoteId);
	const quoteType = useQuoteTypeNameByQuoteId(quoteId);
	const [editOpen, setEditOpen] = React.useState(false);

	const editable = quote?.editable ?? false;
	const { alert, toast } = ROOT;

	const onDelete = () => {
		alert({
			title: 'Are you sure you want to delete this awning?',
			text: `You are about to delete ${displayName}. There's no way to undo this.`,
			icon: 'warning',
			quoteType,
		})
			.then(() => {
				dispatch(deleteQuoteAwning({ quoteId, lineNumber }))
					.unwrap()
					.then(() => {
						toast(`${displayName} deleted`, { type: 'success' });
					})
					.catch(() => {
						toast(`Deletion of ${displayName} failed`, { type: 'info' });
					});
			})
			.catch(() => {
				toast(`Deletion of ${displayName} canceled`, { type: 'info' });
			});
	};

	const buttons: React.ReactNode[] = [];

	if (editable) {
		buttons.push(<Col key="edit">
			<EditAwningModal
				quoteId={quoteId}
				lineNumber={lineNumber}
				isOpen={editOpen}
				onClose={() => setEditOpen(false)}
			/>
			<Button
				title="Edit"
				color="light"
				size="sm"
				fullWidth
				hoverColor={quoteType}
				onClick={() => setEditOpen(true)}
			>
				<FontAwesomeIcon icon={faEdit} />
			</Button>
		</Col>);

		buttons.push(<Col key="duplicate">
			<AwningDuplicate quoteId={quoteId} lineNumber={lineNumber} />
		</Col>);

		buttons.push(<Col key="delete">
			<Button
				title="Delete"
				color="light"
				size="sm"
				fullWidth
				hoverColor={quoteType}
				onClick={onDelete}
			>
				<FontAwesomeIcon icon={faTrashAlt} />
			</Button>
		</Col>);
	}

	buttons.push(<AwningSummary key="summary" quoteId={quoteId} lineNumber={lineNumber} />);

	return (
		<div className={`${!editable ? "d-flex" : ""} g-0`} style={{ columnCount: 2, columnGap: 0 }}>
			{buttons}
		</div>
	);
};

export default AwningAction;
